import { useEffect } from "react";
import { Link as RemixLink } from "@remix-run/react";

import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Wrap,
} from "@chakra-ui/react";

import { useUserAvailabilityRequestLimit } from "#app/hooks/index.js";
import { maybePluralizeNoun } from "#app/utils/words.js";
import { PauseIcon } from "#app/components/Icons.jsx";
import { capturePostHog } from "#app/utils/posthog.client.js";

// Shown at form mount when the user is already at their active-request cap.
// The submit-time check (verifyUserForRequestForm) still enforces the cap;
// this banner just surfaces the same pause/upgrade choices before the user
// invests in filling out the form.
export function RequestLimitBanner({
  analyticsProps,
  onOpenLimitModal,
  ...rest
}) {
  const { limit, canSubscribe } = useUserAvailabilityRequestLimit();
  const isShown = !!limit;

  // Pre-submit counterpart to request_form_limit_reached. Fired here, after
  // the render guard, so the event can never fire without the banner
  // actually being shown.
  useEffect(() => {
    if (isShown) {
      capturePostHog("request_form_limit_warning_shown", analyticsProps);
    }
  }, []);

  if (!isShown) return null;

  return (
    <Alert
      status="warning"
      variant="top-accent"
      mb={4}
      data-testid="request-form-limit-banner"
      {...rest}
    >
      <AlertIcon />
      <Box flexGrow={1}>
        <AlertTitle>
          You're using all {maybePluralizeNoun(limit, "active request")}
        </AlertTitle>
        <AlertDescription>
          You can fill out this form now, but this request will need a free slot
          before it can start. Pause a request you're done with
          {canSubscribe ? " or upgrade your account" : ""} to make room.
          <Wrap mt={3} spacing={2}>
            <Button
              size="sm"
              leftIcon={<PauseIcon />}
              onClick={onOpenLimitModal}
            >
              Pause a request
            </Button>
            {canSubscribe && (
              <Button
                size="sm"
                variant="primary"
                as={RemixLink}
                to="/subscribe"
              >
                Upgrade Account
              </Button>
            )}
          </Wrap>
        </AlertDescription>
      </Box>
    </Alert>
  );
}
